import { useState, useEffect } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Building, Edit2, Save } from 'lucide-react';
import { toast } from 'sonner';

const getBackendUrl = () => {
  if (window.location.hostname !== 'localhost') {
    return window.location.origin;
  }
  return process.env.REACT_APP_BACKEND_URL || '';
};

const BACKEND_URL = getBackendUrl();

function CompanyDetails() {
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    phone: '',
    industry: '',
    employee_count: ''
  });

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/companies`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setCompanies(data);
        if (data.length > 0) {
          setFormData({
            name: data[0].name || '',
            address: data[0].address || '',
            phone: data[0].phone || '',
            industry: data[0].industry || '',
            employee_count: data[0].employee_count || ''
          });
        }
      }
    } catch (error) {
      toast.error('Failed to load company details');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      let response;
      if (companies.length > 0) {
        response = await fetch(`${BACKEND_URL}/api/companies/${companies[0].company_id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(formData)
        });
      } else {
        response = await fetch(`${BACKEND_URL}/api/companies`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(formData)
        });
      }
      
      if (response.ok) {
        toast.success('Company details saved successfully');
        setEditing(false);
        fetchCompanies();
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      toast.error('Failed to save company details');
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Company Details">
        <div className="flex items-center justify-center h-64">
          <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Company Details">
      <div className="max-w-3xl" data-testid="company-details-page">
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Building className="h-8 w-8 text-primary" />
              <h2 className="text-2xl font-mono font-bold">Company Information</h2>
            </div>
            {!editing && (
              <Button
                onClick={() => setEditing(true)}
                variant="outline"
                className="font-mono"
                data-testid="edit-company-button"
              >
                <Edit2 className="h-4 w-4 mr-2" />
                Edit
              </Button>
            )}
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="company-form">
            <div>
              <Label htmlFor="name" className="font-mono">Company Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                disabled={!editing}
                required
                className="font-mono mt-1"
                data-testid="company-name-input"
              />
            </div>

            <div>
              <Label htmlFor="address" className="font-mono">Address</Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                disabled={!editing}
                className="font-mono mt-1"
                data-testid="company-address-input"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="phone" className="font-mono">Phone</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  disabled={!editing}
                  className="font-mono mt-1"
                  data-testid="company-phone-input"
                />
              </div>

              <div>
                <Label htmlFor="industry" className="font-mono">Industry</Label>
                <Input
                  id="industry"
                  value={formData.industry}
                  onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                  disabled={!editing}
                  className="font-mono mt-1"
                  data-testid="company-industry-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="employee_count" className="font-mono">Employee Count</Label>
              <Input
                id="employee_count"
                value={formData.employee_count}
                onChange={(e) => setFormData({ ...formData, employee_count: e.target.value })}
                disabled={!editing}
                placeholder="e.g., 1-10, 10-50, 50-100, 100+"
                className="font-mono mt-1"
                data-testid="company-employee-count-input"
              />
            </div>

            {editing && (
              <div className="flex gap-3 pt-4">
                <Button
                  type="submit"
                  className="bg-primary text-primary-foreground hover:bg-primary/90 font-mono"
                  data-testid="save-company-button"
                >
                  <Save className="h-4 w-4 mr-2" />
                  Save Changes
                </Button>
                <Button
                  type="button"
                  onClick={() => {
                    setEditing(false);
                    if (companies.length > 0) {
                      setFormData({
                        name: companies[0].name || '',
                        address: companies[0].address || '',
                        phone: companies[0].phone || '',
                        industry: companies[0].industry || '',
                        employee_count: companies[0].employee_count || ''
                      });
                    }
                  }}
                  variant="outline"
                  className="font-mono"
                  data-testid="cancel-edit-button"
                >
                  Cancel
                </Button>
              </div>
            )}
          </form>
        </div>
      </div>
    </DashboardLayout>
  );
}

export default CompanyDetails;